import React, { useCallback, useId, useState } from "react";
import Popover from "@mui/material/Popover";
import { useTranslation } from "react-i18next";
import downloadPdfIconSvg from "../../assets/images/downloadpdficonsvg.svg";
import downloadPngIconSvg from "../../assets/images/downloadpngiconsvg.svg";
import "./DashboardDownloadMenu.scss";

const DownloadIcon = ({ src, alt = "" }) => (
  <img className="pos-dash-download-menu__icon" src={src} alt={alt} aria-hidden />
);

const defaultNoop = () => {};

/**
 * Download trigger + popover (PDF / PNG). Styling matches dashboard export spec.
 * @param {object} props
 * @param {string} props.buttonClassName — e.g. `pos-dashboard__export` or `pos-total-sales-modal__icon-btn`
 * @param {React.ReactNode} props.children — icon(s) inside the trigger button
 * @param {() => void} [props.onDownloadPdf]
 * @param {() => void} [props.onDownloadPng]
 * @param {string} [props.title] — native title on trigger
 * @param {string} [props.buttonAriaHasPopup] — default "menu"
 */
const DashboardDownloadMenu = ({
  buttonClassName,
  children,
  onDownloadPdf = defaultNoop,
  onDownloadPng = defaultNoop,
  title,
  buttonAriaHasPopup = "menu",
}) => {
  const { t } = useTranslation();
  const menuId = useId();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const close = useCallback(() => setAnchorEl(null), []);

  const onPdf = useCallback(() => {
    Promise.resolve(onDownloadPdf()).finally(close);
  }, [close, onDownloadPdf]);

  const onPng = useCallback(() => {
    Promise.resolve(onDownloadPng()).finally(close);
  }, [close, onDownloadPng]);

  const labelPdf = t("downloadPdf", { defaultValue: "Download PDF" });
  const labelPng = t("downloadPng", { defaultValue: "Download PNG" });
  const labelOpen = t("Export", { defaultValue: "Export" });

  return (
    <>
      <button
        type="button"
        className={buttonClassName}
        title={title ?? labelOpen}
        aria-label={title ?? labelOpen}
        aria-haspopup={buttonAriaHasPopup}
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        id={`${menuId}-trigger`}
        onClick={(e) => {
          setAnchorEl((current) => (current ? null : e.currentTarget));
        }}
      >
        {children}
      </button>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={close}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{ zIndex: (theme) => theme.zIndex.modal + 10 }}
        slotProps={{
          paper: {
            className: "pos-dash-download-menu__paper",
            elevation: 3,
            id: menuId,
            role: "menu",
            "aria-labelledby": `${menuId}-trigger`,
          },
        }}
        marginThreshold={8}
      >
        <ul className="pos-dash-download-menu__list" role="presentation">
          <li className="pos-dash-download-menu__item" role="presentation">
            <button type="button" className="pos-dash-download-menu__item-btn" role="menuitem" onClick={onPdf}>
              <span className="pos-dash-download-menu__item-icon-wrap" aria-hidden>
                <DownloadIcon src={downloadPdfIconSvg} />
              </span>
              <span className="pos-dash-download-menu__item-label">{labelPdf}</span>
            </button>
          </li>
          <li className="pos-dash-download-menu__item" role="presentation">
            <button type="button" className="pos-dash-download-menu__item-btn" role="menuitem" onClick={onPng}>
              <span className="pos-dash-download-menu__item-icon-wrap" aria-hidden>
                <DownloadIcon src={downloadPngIconSvg} />
              </span>
              <span className="pos-dash-download-menu__item-label">{labelPng}</span>
            </button>
          </li>
        </ul>
      </Popover>
    </>
  );
};

export default DashboardDownloadMenu;
